import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";

const BASE = "/api/v1/prospectos";

export type EstadoProspecto =
  | "nuevo"
  | "contactado"
  | "agendado"
  | "convertido"
  | "descartado";

export const ESTADOS: EstadoProspecto[] = [
  "nuevo",
  "contactado",
  "agendado",
  "convertido",
  "descartado",
];

export const ESTADO_LABEL: Record<EstadoProspecto, string> = {
  nuevo: "Nuevo",
  contactado: "Contactado",
  agendado: "Agendado",
  convertido: "Convertido",
  descartado: "Descartado",
};

export const ESTADO_PILL: Record<EstadoProspecto, string> = {
  nuevo: "bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  contactado: "bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
  agendado: "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  convertido: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  descartado: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
};

export type ProspectoSummary = {
  id: string;
  folio: string;
  nombre_completo: string | null;
  telefono: string | null;
  estado: EstadoProspecto;
  capturado: boolean;
  lat: number | null;
  lng: number | null;
  creado_at: string;
};

export type ProspectoDetail = ProspectoSummary & {
  empresa_id: number | null;
  ubicacion_precision_m: number | null;
  ubicacion_capturada_at: string | null;
  captura_token: string | null;
  token_expira_at: string | null;
  origen: string;
  notas: string | null;
  convertido_cliente_id: string | null;
  actualizado_at: string;
};

export type ProspectoListResponse = {
  items: ProspectoSummary[];
  total: number;
  page: number;
  size: number;
};

export type ProspectosFilters = {
  page?: number;
  size?: number;
  estado?: EstadoProspecto;
  q?: string;
};

export type LinkOut = { captura_token: string; token_expira_at: string };

export function useListProspectos(filters: ProspectosFilters) {
  return useQuery({
    queryKey: ["prospectos", filters],
    queryFn: () =>
      apiCall<ProspectoListResponse>("get", BASE, {
        searchParams: {
          page: filters.page ?? 1,
          size: filters.size ?? 25,
          ...(filters.estado ? { estado: filters.estado } : {}),
          ...(filters.q ? { q: filters.q } : {}),
        },
      }),
  });
}

export function useProspecto(id: string | undefined) {
  return useQuery({
    queryKey: ["prospecto", id],
    queryFn: () => apiCall<ProspectoDetail>("get", `${BASE}/${id}`),
    enabled: !!id,
  });
}

export function useCreateProspecto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { nombre_completo?: string; telefono?: string; notas?: string }) =>
      apiCall<ProspectoDetail>("post", BASE, { json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prospectos"] }),
  });
}

export function useUpdateProspecto(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<{
      nombre_completo: string;
      telefono: string;
      estado: EstadoProspecto;
      notas: string;
    }>) => apiCall<ProspectoDetail>("patch", `${BASE}/${id}`, { json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prospecto", id] });
      qc.invalidateQueries({ queryKey: ["prospectos"] });
    },
  });
}

export function useGenerarLink(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiCall<LinkOut>("post", `${BASE}/${id}/generar-link`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prospecto", id] });
      qc.invalidateQueries({ queryKey: ["prospectos"] });
    },
  });
}

/** Genera el link de un prospecto recién creado (sin hook ligado a un id). */
export function generarLink(id: string): Promise<LinkOut> {
  return apiCall<LinkOut>("post", `${BASE}/${id}/generar-link`);
}

/** rc145 — liga el prospecto al cliente creado desde "Convertir en cliente". */
export function useConvertirProspecto(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cliente_id: string) =>
      apiCall<ProspectoDetail>("post", `${BASE}/${id}/convertir`, { json: { cliente_id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prospecto", id] });
      qc.invalidateQueries({ queryKey: ["prospectos"] });
    },
  });
}

export function useDeleteProspecto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiCall<void>("delete", `${BASE}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prospectos"] }),
  });
}

/** Arma la URL pública absoluta del link de captura (mismo origen que el admin). */
export function capturaUrl(token: string): string {
  return `${window.location.origin}/captura/${token}`;
}
